import { useEffect, useMemo, useState } from "react";
import { Box, Plus, Trash2 } from "lucide-react";
import { inputCls } from "../pages/Login";

const boxCw = (b) => {
  const w = parseFloat(b.weight) || 0;
  const vol = b.length && b.width && b.height ? (parseFloat(b.length) * parseFloat(b.width) * parseFloat(b.height)) / 5000 : 0;
  // Billed in whole kg (rounded UP) — matches the backend chargeable weight exactly
  return Math.max(Math.ceil(Math.max(w, vol, 0.1) - 1e-9), 1);
};

export default function BoxFields({ totalWeight, onChange }) {
  const heavy = parseFloat(totalWeight) > 10;
  const [mps, setMps] = useState(false);
  const [boxes, setBoxes] = useState([{ weight: "", length: "", width: "", height: "" }]);

  const effectiveMps = heavy && mps;
  const total = useMemo(() => boxes.reduce((s, b) => s + boxCw(b), 0), [boxes]);

  useEffect(() => {
    onChange?.(effectiveMps
      ? { package_type: "MPS", boxes, chargeable: total }
      : { package_type: "SPS", boxes: null, chargeable: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveMps, boxes, total]);

  const setBox = (i, k) => (e) => {
    const next = [...boxes];
    next[i] = { ...next[i], [k]: e.target.value };
    setBoxes(next);
  };

  return (
    <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="box-fields">
      {!heavy ? (
        <p className="flex items-center gap-2 text-xs font-semibold text-slate-600">
          <Box className="h-4 w-4 text-saffron" /> Single Box Shipment (SPS) — up to 10 kg ships as one box
        </p>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Heavy shipment (&gt;10 kg) · package type:</span>
            <div className="flex rounded-full border border-slate-300 bg-white p-0.5">
              <button type="button" data-testid="pkg-type-single" onClick={() => setMps(false)}
                      className={`rounded-full px-4 py-1.5 text-xs font-bold transition-colors ${!mps ? "bg-navy text-white" : "text-slate-500"}`}>
                Single Box
              </button>
              <button type="button" data-testid="pkg-type-multi" onClick={() => setMps(true)}
                      className={`rounded-full px-4 py-1.5 text-xs font-bold transition-colors ${mps ? "bg-saffron text-white" : "text-slate-500"}`}>
                Multiple Boxes
              </button>
            </div>
          </div>
          {effectiveMps && (
            <div className="mt-3 space-y-3">
              {boxes.map((b, i) => (
                <div key={i} data-testid={`box-row-${i}`}
                     className="grid grid-cols-2 items-end gap-2 rounded-lg border border-slate-200 bg-white p-3 md:grid-cols-6">
                  <p className="col-span-2 font-heading text-xs font-bold text-navy md:col-span-1">Box {i + 1}</p>
                  {[["weight", "Wt (kg)"], ["length", "L (cm)"], ["width", "W (cm)"], ["height", "H (cm)"]].map(([k, label]) => (
                    <div key={k}>
                      <label className="mb-0.5 block text-[10px] font-semibold uppercase text-slate-400">{label}</label>
                      <input type="number" step="0.1" min="0" className={inputCls} data-testid={`box-${i}-${k}`}
                             value={b[k]} onChange={setBox(i, k)} />
                    </div>
                  ))}
                  <div className="col-span-2 flex items-center justify-between gap-2 md:col-span-1">
                    <span className="rounded-full bg-saffron-light px-2 py-1 text-[10px] font-bold text-saffron" data-testid={`box-${i}-billed`}>
                      Billed {boxCw(b)} kg
                    </span>
                    {boxes.length > 1 && (
                      <button type="button" onClick={() => setBoxes(boxes.filter((_, j) => j !== i))}
                              data-testid={`box-${i}-remove`} className="text-red-400 hover:text-red-600" aria-label="Remove box">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
              <div className="flex flex-wrap items-center justify-between gap-3">
                <button type="button" onClick={() => setBoxes([...boxes, { weight: "", length: "", width: "", height: "" }])}
                        data-testid="box-add-btn"
                        className="flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3.5 py-2 text-xs font-bold text-navy hover:border-saffron hover:text-saffron">
                  <Plus className="h-3.5 w-3.5" /> Add Box
                </button>
                <span className="rounded-full bg-navy px-3.5 py-1.5 text-xs font-bold text-white" data-testid="mps-total-billed">
                  Total billed weight: {total} kg
                </span>
              </div>
              <p className="text-[11px] text-slate-400">Each box is billed on the higher of actual vs volumetric weight, rounded up to the next whole kg; all boxes are summed for the multipiece (MPS) rate.</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
